import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePlay } from '../contexts/PlayContext';
import { useModal } from './Modal';
import ScoresTable from './ScoresTable';
import ManageFriends from './ManageFriends';
import SharePanel from './SharePanel';
import SectionBlock from './SectionBlock';
import { trackViewFriends } from '../services/analyticsService';

// "Scores" - today's results next to your friends', plus the two things you
// can do about them: manage who's on the list, and invite someone new.
//
// **It hides unless somebody has actually scored today** - the caller or any
// friend. A table of empty columns is an empty room with the lights on, and a
// play row alone isn't a score (one is written on any game-link click, D30),
// so this tests the score itself rather than the row.
//
// **One exception, and it's load-bearing:** the share panel lives in here, and
// the ladder's state-4 message links to `/#share`. If the block could hide
// while that message was on screen, that link would point at nothing. So an
// explicit request for the share panel reveals the section whatever else is
// going on. Closing it hides the section again if there's still nothing to
// show.
//
// "Manage friends" and "Invite" are mutually exclusive: opening one closes the
// other, so the section never shows two editors at once.
//
// **Sharing expands the block rather than replacing its contents.** The scores
// table stays put and a row of share buttons opens in height beneath the
// footer - the button that opened it is still on screen and toggles it shut.
// Only "Manage friends" swaps the table out for its own editor.

// A play row exists for any game opened; only some carry a score.
const hasAnyScore = (todayPlays) => Object.values(todayPlays || {}).some(
  (play) => play && play.score !== null && play.score !== undefined && play.score !== ''
);

// Opens its children in height. The grid-rows 0fr -> 1fr trick animates to the
// content's natural height without measuring it. Mounts collapsed and flips on
// the next frame, so the transition has a starting state to run from; closing
// simply unmounts (the parent stops rendering it).
function Expand({ children }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <div
      className={`grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none ${
        open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
      }`}
    >
      <div className="min-h-0 overflow-hidden">{children}</div>
    </div>
  );
}

function ScoresSection() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    addFriend,
    removeFriend,
    friendsData,
    friendsLoading,
    friendsError,
    loadFriendsData,
    gamesPlayedToday
  } = usePlay();
  const { showModal } = useModal();
  const [panel, setPanel] = useState(null);

  const friendCount = friendsData?.friends?.length || 0;

  // PlayContext already loads the dashboard when the identity settles, so
  // this only reports the view. Depending on gamesPlayedToday.length rather
  // than the array keeps it from re-firing on every reload.
  const playedTodayCount = gamesPlayedToday.length;
  useEffect(() => {
    if (!user) return;
    trackViewFriends(playedTodayCount);
  }, [user, playedTodayCount]);

  // The ladder's "invite your friends" message links to /#share rather than
  // navigating anywhere; this is the receiving half of that.
  useEffect(() => {
    if (location.hash === '#share') setPanel('share');
  }, [location.hash]);

  // Nothing to show before there's a record to show it for.
  if (!user) return null;

  const anyScoresToday = hasAnyScore(friendsData?.user?.today_plays)
    || (friendsData?.friends || []).some((friend) => hasAnyScore(friend.today_plays));

  // Hidden when there's nothing in the table - except when the share panel was
  // asked for (see above), or when the load failed, which keeps Retry
  // reachable rather than making a broken fetch look like an empty day.
  if (!anyScoresToday && panel !== 'share' && !friendsError) return null;

  const togglePanel = (name) => {
    const next = panel === name ? null : name;
    setPanel(next);

    // Clear the hash on the way out of the share panel. Without this, a second
    // click on the ladder's "/#share" link is a no-op - the hash wouldn't
    // change, so the effect above wouldn't re-fire, and with the section
    // hidden there'd be no other way back in.
    if (next !== 'share' && location.hash === '#share') {
      navigate(location.pathname, { replace: true });
    }
  };

  return (
    <SectionBlock title="Scores">
      {friendsLoading && (
        <p className="px-2 py-4 text-sm text-nlg-muted text-center">Loading scores...</p>
      )}

      {!friendsLoading && friendsError && (
        <div className="px-2 py-4 text-center">
          <p className="text-sm text-nlg-coral mb-2">Couldn't load scores.</p>
          <button
            onClick={() => loadFriendsData()}
            className="text-sm font-bold text-nlg-gold"
          >
            Retry
          </button>
        </div>
      )}

      {!friendsLoading && !friendsError && panel !== 'friends' && (
        <ScoresTable friendsData={friendsData} showModal={showModal} />
      )}

      {panel === 'friends' && (
        <div className="px-3 py-3">
          <ManageFriends
            friendsData={friendsData}
            onAddFriend={addFriend}
            onRemoveFriend={removeFriend}
            onChanged={() => loadFriendsData(true)}
          />
        </div>
      )}

      {/* Only offered when there are friends to manage. The old "Add
          friends" branch opened a modal with no form in it - add-by-username
          is retired (D39), and connecting happens through Invite now. */}
      {friendCount > 0 && (
        <div className="px-4 py-1.5 border-t border-nlg-rule">
          <button
            onClick={() => togglePanel('friends')}
            className="text-sm font-bold text-nlg-gold transition-colors duration-150"
          >
            {panel === 'friends' ? 'Done editing' : 'Manage friends'}
          </button>
        </div>
      )}

      {/* Invite and Stats. The share button stays put while its panel is open
          and toggles it shut. */}
      <div className="px-2 py-2 border-t border-nlg-rule flex items-stretch gap-2">
        <button
          onClick={() => togglePanel('share')}
          aria-expanded={panel === 'share'}
          className="flex-[3] min-w-0 flex items-center gap-3 bg-nlg-gold text-nlg-bg rounded-xl px-1 py-1 text-left transition-transform duration-150 hover:scale-[1.02]"
        >
          <img src="/img/trophy.png" alt="" className="w-12 h-12 rounded-lg border-2 border-black flex-shrink-0" />
          <span className="font-display font-bold text-sm leading-tight">Share scores and play with friends</span>
        </button>

        <Link
          to="/stats"
          className="flex-[2] min-w-0 flex items-center gap-3 bg-nlg-gold text-nlg-bg rounded-xl px-1 py-1 transition-transform duration-150 hover:scale-[1.02]"
        >
          <img src="/img/stats.png" alt="" className="w-12 h-12 rounded-lg border-2 border-black flex-shrink-0" />
          <span className="font-display font-bold text-sm leading-tight">Score history</span>
        </Link>
      </div>

      {panel === 'share' && (
        <Expand>
          <div className="border-t border-nlg-rule">
            <SharePanel />
          </div>
        </Expand>
      )}
    </SectionBlock>
  );
}

export default ScoresSection;
